/**
 * Specification Diff View Component
 * @implements FS-004.3.1 - Edit Specification
 * @implements US-059 - Specification Editing with Diff View
 *
 * Renders a structured, section-aware diff between the previous and current
 * versions of a specification. Uses color-coding to indicate changes:
 * - Green for additions
 * - Red for removals
 * - Blue for modifications
 * - Grey for unchanged content
 */

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  CompareArrows as CompareArrowsIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { useQuery, gql } from '@apollo/client';

// ============================================================================
// Types
// ============================================================================

/**
 * Change type enum matching backend DiffChangeType
 * @implements FS-004.3.1
 */
export type DiffChangeType = 'UNCHANGED' | 'ADDED' | 'REMOVED' | 'MODIFIED';

/**
 * Text-level diff for a single string field
 * @implements FS-004.3.1
 */
export interface TextDiff {
  changeType: DiffChangeType;
  previousValue: string | null;
  currentValue: string | null;
}

/**
 * Functional requirement from the spec
 */
interface FunctionalRequirement {
  id: string;
  title: string;
  description: string;
  acceptanceCriteria: string[];
}

/**
 * Diff for a single functional requirement
 * @implements FS-004.3.1
 */
export interface FunctionalRequirementDiff {
  id: string;
  changeType: DiffChangeType;
  previous: FunctionalRequirement | null;
  current: FunctionalRequirement | null;
}

/**
 * Diff for business rule ID changes
 * @implements FS-004.3.1
 */
export interface BusinessRuleIdsDiff {
  added: string[];
  removed: string[];
}

/**
 * Structured diff between two specification versions
 * @implements FS-004.3.1
 */
export interface SpecificationDiff {
  hasChanges: boolean;
  overview: TextDiff;
  functionalRequirements: FunctionalRequirementDiff[];
  businessRuleIds: BusinessRuleIdsDiff;
  dataRequirements: TextDiff;
  userInteractions: TextDiff;
  edgeCases: TextDiff;
  dependencies: TextDiff;
}

// ============================================================================
// GraphQL Query
// ============================================================================

/**
 * @implements US-059 - Fetch structured diff from backend
 */
const GET_SPECIFICATION_DIFF_QUERY = gql`
  query GetSpecificationDiff($id: ID!) {
    specificationDiff(id: $id) {
      hasChanges
      overview {
        changeType
        previousValue
        currentValue
      }
      functionalRequirements {
        id
        changeType
        previous {
          id
          title
          description
          acceptanceCriteria
        }
        current {
          id
          title
          description
          acceptanceCriteria
        }
      }
      businessRuleIds {
        added
        removed
      }
      dataRequirements {
        changeType
        previousValue
        currentValue
      }
      userInteractions {
        changeType
        previousValue
        currentValue
      }
      edgeCases {
        changeType
        previousValue
        currentValue
      }
      dependencies {
        changeType
        previousValue
        currentValue
      }
    }
  }
`;

interface GetSpecificationDiffResponse {
  specificationDiff: SpecificationDiff;
}

interface GetSpecificationDiffVariables {
  id: string;
}

// ============================================================================
// Helper Components
// ============================================================================

/**
 * Color and icon mapping for change types
 */
const changeTypeConfig: Record<
  DiffChangeType,
  {
    color: string;
    bgColor: string;
    borderColor: string;
    label: string;
    icon: React.ReactNode;
  }
> = {
  ADDED: {
    color: 'success.dark',
    bgColor: 'success.50',
    borderColor: 'success.main',
    label: 'Added',
    icon: <AddIcon fontSize="small" />,
  },
  REMOVED: {
    color: 'error.dark',
    bgColor: 'error.50',
    borderColor: 'error.main',
    label: 'Removed',
    icon: <RemoveIcon fontSize="small" />,
  },
  MODIFIED: {
    color: 'info.dark',
    bgColor: 'info.50',
    borderColor: 'info.main',
    label: 'Modified',
    icon: <EditIcon fontSize="small" />,
  },
  UNCHANGED: {
    color: 'text.secondary',
    bgColor: 'grey.50',
    borderColor: 'grey.300',
    label: 'Unchanged',
    icon: null,
  },
};

/**
 * Badge showing the change type
 */
const ChangeTypeBadge = ({ changeType }: { changeType: DiffChangeType }) => {
  if (changeType === 'UNCHANGED') return null;

  const config = changeTypeConfig[changeType];
  return (
    <Chip
      label={config.label}
      size="small"
      icon={config.icon as React.ReactElement}
      sx={{
        bgcolor:
          changeType === 'ADDED'
            ? 'success.light'
            : changeType === 'REMOVED'
            ? 'error.light'
            : 'info.light',
        color:
          changeType === 'ADDED'
            ? 'success.dark'
            : changeType === 'REMOVED'
            ? 'error.dark'
            : 'info.dark',
        fontWeight: 500,
        fontSize: '0.75rem',
      }}
    />
  );
};

/**
 * Renders a text diff with previous/current values
 * @implements US-059 - Color-coded diff rendering
 */
const TextDiffView = ({ diff, label }: { diff: TextDiff; label: string }) => {
  if (diff.changeType === 'UNCHANGED') return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {label}
        </Typography>
        <ChangeTypeBadge changeType={diff.changeType} />
      </Stack>

      {diff.changeType === 'REMOVED' && diff.previousValue && (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: '#ffebee',
            borderLeft: '3px solid',
            borderLeftColor: 'error.main',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              textDecoration: 'line-through',
              color: 'error.dark',
              whiteSpace: 'pre-wrap',
            }}
          >
            {diff.previousValue}
          </Typography>
        </Box>
      )}

      {diff.changeType === 'ADDED' && diff.currentValue && (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: '#e8f5e9',
            borderLeft: '3px solid',
            borderLeftColor: 'success.main',
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: 'success.dark', whiteSpace: 'pre-wrap' }}
          >
            {diff.currentValue}
          </Typography>
        </Box>
      )}

      {diff.changeType === 'MODIFIED' && (
        <Stack spacing={1}>
          {diff.previousValue && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: '#ffebee',
                borderLeft: '3px solid',
                borderLeftColor: 'error.main',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'error.dark',
                  fontWeight: 600,
                  display: 'block',
                  mb: 0.5,
                }}
              >
                Previous:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  textDecoration: 'line-through',
                  color: 'error.dark',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {diff.previousValue}
              </Typography>
            </Box>
          )}
          {diff.currentValue && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: '#e8f5e9',
                borderLeft: '3px solid',
                borderLeftColor: 'success.main',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'success.dark',
                  fontWeight: 600,
                  display: 'block',
                  mb: 0.5,
                }}
              >
                Current:
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'success.dark', whiteSpace: 'pre-wrap' }}
              >
                {diff.currentValue}
              </Typography>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
};

/**
 * Renders a single functional requirement diff
 * @implements US-059 - Per-requirement change display
 */
const FunctionalRequirementDiffView = ({
  diff,
}: {
  diff: FunctionalRequirementDiff;
}) => {
  if (diff.changeType === 'UNCHANGED') return null;

  const config = changeTypeConfig[diff.changeType];
  const req = diff.current || diff.previous;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: config.borderColor,
        borderLeft: '4px solid',
        borderLeftColor: config.borderColor,
        bgcolor:
          diff.changeType === 'ADDED'
            ? '#e8f5e9'
            : diff.changeType === 'REMOVED'
            ? '#ffebee'
            : '#e3f2fd',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Chip
          label={diff.id}
          size="small"
          sx={{ fontFamily: 'monospace', fontWeight: 600 }}
        />
        <Typography variant="subtitle2" fontWeight={500}>
          {req?.title ?? '(no title)'}
        </Typography>
        <ChangeTypeBadge changeType={diff.changeType} />
      </Stack>

      {diff.changeType === 'MODIFIED' && diff.previous && diff.current && (
        <Box sx={{ mt: 1 }}>
          {diff.previous.title !== diff.current.title && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
              >
                Title:
              </Typography>
              <Typography
                variant="body2"
                sx={{ textDecoration: 'line-through', color: 'error.dark' }}
              >
                {diff.previous.title}
              </Typography>
              <Typography variant="body2" sx={{ color: 'success.dark' }}>
                {diff.current.title}
              </Typography>
            </Box>
          )}
          {diff.previous.description !== diff.current.description && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
              >
                Description:
              </Typography>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: '#ffebee',
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: 'line-through',
                    color: 'error.dark',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {diff.previous.description}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: '#e8f5e9',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: 'success.dark', whiteSpace: 'pre-wrap' }}
                >
                  {diff.current.description}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {diff.changeType === 'REMOVED' && diff.previous && (
        <Typography
          variant="body2"
          sx={{
            textDecoration: 'line-through',
            color: 'error.dark',
            whiteSpace: 'pre-wrap',
          }}
        >
          {diff.previous.description}
        </Typography>
      )}

      {diff.changeType === 'ADDED' && diff.current && (
        <Typography
          variant="body2"
          sx={{ color: 'success.dark', whiteSpace: 'pre-wrap' }}
        >
          {diff.current.description}
        </Typography>
      )}
    </Paper>
  );
};

// ============================================================================
// Main Component
// ============================================================================

/**
 * Props for SpecificationDiffView
 */
export interface SpecificationDiffViewProps {
  /** The specification ID to fetch the diff for */
  specificationId: string;
  /** Callback to dismiss/hide the diff view */
  onDismiss: () => void;
}

/**
 * SpecificationDiffView - Renders a structured diff for a specification
 *
 * @implements FS-004.3.1 - Edit Specification (diff highlighting)
 * @implements US-059 - Specification Editing with Diff View
 *
 * Features:
 * - Fetches structured diff from backend via `specificationDiff` query
 * - Color-coded display: green (additions), red (removals), blue (modifications)
 * - Section-aware rendering for all specification content fields
 * - Per-requirement diff for functional requirements
 * - Business rule ID changes (added/removed)
 * - "No changes" message when diff reports all UNCHANGED
 * - Dismiss button to hide the diff view
 * - Collapsible sections for long diffs
 */
export const SpecificationDiffView = ({
  specificationId,
  onDismiss,
}: SpecificationDiffViewProps) => {
  const [expanded, setExpanded] = useState(true);

  const { data, loading, error } = useQuery<
    GetSpecificationDiffResponse,
    GetSpecificationDiffVariables
  >(GET_SPECIFICATION_DIFF_QUERY, {
    variables: { id: specificationId },
    fetchPolicy: 'cache-and-network',
  });

  const diff = data?.specificationDiff;

  // Loading state
  if (loading && !data) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          border: '1px solid',
          borderColor: 'info.main',
          bgcolor: '#e3f2fd',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={20} />
          <Typography variant="body2">Loading diff...</Typography>
        </Stack>
      </Paper>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert
        severity="warning"
        sx={{ mb: 3 }}
        action={
          <Button color="inherit" size="small" onClick={onDismiss}>
            Dismiss
          </Button>
        }
      >
        Unable to load diff: {error.message}
      </Alert>
    );
  }

  // No diff data
  if (!diff) {
    return null;
  }

  // No changes
  if (!diff.hasChanges) {
    return (
      <Alert
        severity="info"
        sx={{ mb: 3 }}
        action={
          <Button color="inherit" size="small" onClick={onDismiss}>
            Dismiss
          </Button>
        }
      >
        No changes were made to this specification.
      </Alert>
    );
  }

  // Count changes for summary
  const textSections: { label: string; diff: TextDiff }[] = [
    { label: 'Overview', diff: diff.overview },
    { label: 'Data Requirements', diff: diff.dataRequirements },
    { label: 'User Interactions', diff: diff.userInteractions },
    { label: 'Edge Cases', diff: diff.edgeCases },
    { label: 'Dependencies', diff: diff.dependencies },
  ];

  const changedTextSections = textSections.filter(
    (s) => s.diff.changeType !== 'UNCHANGED',
  );

  const changedRequirements = diff.functionalRequirements.filter(
    (r) => r.changeType !== 'UNCHANGED',
  );

  const hasBusinessRuleChanges =
    diff.businessRuleIds.added.length > 0 ||
    diff.businessRuleIds.removed.length > 0;

  const totalChanges =
    changedTextSections.length +
    changedRequirements.length +
    (hasBusinessRuleChanges ? 1 : 0);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        border: '1px solid',
        borderColor: 'info.main',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: '#e3f2fd',
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: 'info.light',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <CompareArrowsIcon color="info" />
            <Typography variant="subtitle1" fontWeight={600} color="info.dark">
              Recent Changes
            </Typography>
            <Chip
              label={`${totalChanges} section${
                totalChanges !== 1 ? 's' : ''
              } changed`}
              size="small"
              color="info"
              variant="outlined"
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              color="inherit"
              startIcon={<CloseIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onDismiss();
              }}
              sx={{ color: 'text.secondary' }}
            >
              Dismiss
            </Button>
            {expanded ? (
              <ExpandLessIcon color="action" />
            ) : (
              <ExpandMoreIcon color="action" />
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Diff Content */}
      <Collapse in={expanded}>
        <Box sx={{ p: 3 }}>
          {/* Text section diffs */}
          {changedTextSections.map((section, index) => (
            <Box key={section.label}>
              <TextDiffView diff={section.diff} label={section.label} />
              {index < changedTextSections.length - 1 && (
                <Divider sx={{ my: 2 }} />
              )}
            </Box>
          ))}

          {/* Functional requirements diffs */}
          {changedRequirements.length > 0 && (
            <>
              {changedTextSections.length > 0 && <Divider sx={{ my: 2 }} />}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ mb: 1.5 }}
                >
                  Functional Requirements
                </Typography>
                <Stack spacing={2}>
                  {changedRequirements.map((reqDiff) => (
                    <FunctionalRequirementDiffView
                      key={reqDiff.id}
                      diff={reqDiff}
                    />
                  ))}
                </Stack>
              </Box>
            </>
          )}

          {/* Business rule ID changes */}
          {hasBusinessRuleChanges && (
            <>
              {(changedTextSections.length > 0 ||
                changedRequirements.length > 0) && <Divider sx={{ my: 2 }} />}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Business Rules
                </Typography>
                <Stack spacing={1}>
                  {diff.businessRuleIds.added.length > 0 && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ChangeTypeBadge changeType="ADDED" />
                      <Typography variant="body2">
                        {diff.businessRuleIds.added.length} business rule
                        {diff.businessRuleIds.added.length !== 1
                          ? 's'
                          : ''}{' '}
                        added
                      </Typography>
                    </Stack>
                  )}
                  {diff.businessRuleIds.removed.length > 0 && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ChangeTypeBadge changeType="REMOVED" />
                      <Typography variant="body2">
                        {diff.businessRuleIds.removed.length} business rule
                        {diff.businessRuleIds.removed.length !== 1
                          ? 's'
                          : ''}{' '}
                        removed
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};
